<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Claim Details - {{ claim.claim_number }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else>
            <!-- Claim Header -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="d-flex align-items-start">
                  <div class="avatar-lg me-3">
                    <div class="avatar-title bg-primary text-white rounded-circle">
                      {{ getInitials(claim.first_name, claim.surname) }}
                    </div>
                  </div>
                  <div>
                    <h4 class="mb-1">{{ claim.first_name }} {{ claim.surname }}</h4>
                    <p class="text-muted mb-1">
                      <i class="bx bx-id-card me-1"></i>CBHI: <strong>{{ claim.cbhi_number }}</strong>
                    </p>
                    <p class="text-muted mb-0">
                      <i class="bx bx-map me-1"></i>{{ claim.region_name }}, {{ claim.woreda_name }}
                    </p>
                    <div class="mt-2">
                      <span v-if="claim.member_active" class="badge bg-success">
                        <i class="bx bx-check-circle me-1"></i>Active Member
                      </span>
                      <span v-else class="badge bg-danger">
                        <i class="bx bx-x-circle me-1"></i>Inactive Member
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-md-end">
                <div class="mb-2">
                  <span :class="getStatusBadge(claim.status) + ' fs-6 px-3 py-2'">
                    {{ claim.status.toUpperCase() }}
                  </span>
                </div>
                <h2 class="text-primary mb-0">ETB {{ formatAmount(claim.total_amount) }}</h2>
                <p class="text-muted">Claimed Amount</p>
                <div v-if="claim.status === 'approved'">
                  <h4 class="text-success mb-0">ETB {{ formatAmount(claim.approved_amount) }}</h4>
                  <p class="text-muted mb-0">Approved Amount</p>
                </div>
              </div>
            </div>

            <!-- Tabs -->
            <ul class="nav nav-tabs nav-tabs-custom nav-justified" role="tablist">
              <li class="nav-item">
                <a class="nav-link active" data-bs-toggle="tab" href="#claim-info" role="tab">
                  <span class="d-block d-sm-none"><i class="fas fa-home"></i></span>
                  <span class="d-none d-sm-block">Claim Information</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#services" role="tab">
                  <span class="d-block d-sm-none"><i class="far fa-user"></i></span>
                  <span class="d-none d-sm-block">Services Provided</span>
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#member-utilization" role="tab">
                  <span class="d-block d-sm-none"><i class="far fa-envelope"></i></span>
                  <span class="d-none d-sm-block">Member Utilization</span>
                </a>
              </li>
              <li class="nav-item" v-if="claim.hospital_claim_reference">
                <a class="nav-link" data-bs-toggle="tab" href="#hospital-docs" role="tab">
                  <span class="d-block d-sm-none"><i class="fas fa-cog"></i></span>
                  <span class="d-none d-sm-block">Hospital Documents</span>
                </a>
              </li>
            </ul>

            <!-- Tab Content -->
            <div class="tab-content p-3 text-muted">
              <!-- Claim Information Tab -->
              <div class="tab-pane active" id="claim-info" role="tabpanel">
                <div class="row">
                  <div class="col-md-6">
                    <div class="card border">
                      <div class="card-header bg-light">
                        <h6 class="mb-0"><i class="bx bx-info-circle me-1"></i>Claim Details</h6>
                      </div>
                      <div class="card-body">
                        <table class="table table-borderless table-sm">
                          <tr>
                            <td class="text-muted" width="40%">Claim Number:</td>
                            <td><strong>{{ claim.claim_number }}</strong></td>
                          </tr>
                          <tr>
                            <td class="text-muted">Hospital Reference:</td>
                            <td>{{ claim.hospital_claim_reference || '-' }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Claim Type:</td>
                            <td>{{ claim.type_name }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Service Date:</td>
                            <td>{{ formatDate(claim.service_date) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Submission Date:</td>
                            <td>{{ formatDate(claim.claim_date) }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Days Pending:</td>
                            <td>
                              <span :class="getPriorityBadge(claim.days_pending)">
                                {{ claim.days_pending }} days
                              </span>
                            </td>
                          </tr>
                          <tr v-if="claim.beneficiary_id">
                            <td class="text-muted">Beneficiary:</td>
                            <td>{{ claim.beneficiary_first_name }} {{ claim.beneficiary_surname }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="card border">
                      <div class="card-header bg-light">
                        <h6 class="mb-0"><i class="bx bx-building-house me-1"></i>Hospital Information</h6>
                      </div>
                      <div class="card-body">
                        <table class="table table-borderless table-sm">
                          <tr>
                            <td class="text-muted" width="40%">Hospital Name:</td>
                            <td><strong>{{ claim.hospital_name }}</strong></td>
                          </tr>
                          <tr>
                            <td class="text-muted">License Number:</td>
                            <td>{{ claim.license_number }}</td>
                          </tr>
                          <tr>
                            <td class="text-muted">Location:</td>
                            <td>{{ claim.zone_name }}, {{ claim.woreda_name }}</td>
                          </tr>
                        </table>
                      </div>
                    </div>

                    <div class="card border mt-3">
                      <div class="card-header bg-light">
                        <h6 class="mb-0"><i class="bx bx-first-aid me-1"></i>Medical Information</h6>
                      </div>
                      <div class="card-body">
                        <div class="mb-3">
                          <label class="text-muted">Diagnosis:</label>
                          <p class="mb-0">{{ claim.diagnosis || '-' }}</p>
                        </div>
                        <div>
                          <label class="text-muted">Treatment Description:</label>
                          <p class="mb-0">{{ claim.treatment_description || '-' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Financial Summary -->
                <div class="row mt-3">
                  <div class="col-12">
                    <div class="card border">
                      <div class="card-header bg-light">
                        <h6 class="mb-0"><i class="bx bx-money me-1"></i>Financial Summary</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-3">
                            <div class="text-center">
                              <h5 class="mb-0">ETB {{ formatAmount(claim.total_amount) }}</h5>
                              <p class="text-muted mb-0">Claimed Amount</p>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="text-center">
                              <h5 class="mb-0 text-success">ETB {{ formatAmount(claim.approved_amount || 0) }}</h5>
                              <p class="text-muted mb-0">Approved Amount</p>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="text-center">
                              <h5 class="mb-0 text-warning">ETB {{ formatAmount(claim.patient_contribution || 0) }}</h5>
                              <p class="text-muted mb-0">Patient Contribution</p>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="text-center">
                              <h5 class="mb-0 text-info">ETB {{ formatAmount(claim.remaining_balance) }}</h5>
                              <p class="text-muted mb-0">Member Balance</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Services Tab -->
              <div class="tab-pane" id="services" role="tabpanel">
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead>
                    <tr>
                      <th>#</th>
                      <th>Service Name</th>
                      <th class="text-center">Quantity</th>
                      <th class="text-end">Unit Price</th>
                      <th class="text-end">Total Price</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(service, index) in claim.services" :key="index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ service.service_name }}</td>
                      <td class="text-center">{{ service.quantity }}</td>
                      <td class="text-end">ETB {{ formatAmount(service.unit_price) }}</td>
                      <td class="text-end">ETB {{ formatAmount(service.total_price) }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                      <th colspan="4" class="text-end">Total:</th>
                      <th class="text-end">ETB {{ formatAmount(claim.total_amount) }}</th>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <!-- Member Utilization Tab -->
              <div class="tab-pane" id="member-utilization" role="tabpanel">
                <div v-if="utilizationLoading" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                <div v-else-if="memberUtilization">
                  <!-- Utilization Overview -->
                  <div class="row mb-4">
                    <div class="col-md-4">
                      <div class="card border">
                        <div class="card-body text-center">
                          <h4 class="mb-0">{{ memberUtilization.utilization.utilization_rate }}%</h4>
                          <p class="text-muted mb-0">Utilization Rate</p>
                          <div class="progress mt-3" style="height: 10px;">
                            <div class="progress-bar" role="progressbar"
                                 :style="{width: memberUtilization.utilization.utilization_rate + '%'}"
                                 :class="getUtilizationBarClass(memberUtilization.utilization.utilization_rate)">
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="card border">
                        <div class="card-body text-center">
                          <h4 class="mb-0 text-primary">ETB {{ formatAmount(memberUtilization.utilization.consumed_amount) }}</h4>
                          <p class="text-muted mb-0">Consumed Amount</p>
                          <small class="text-muted">of ETB {{ formatAmount(memberUtilization.utilization.annual_limit) }}</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4">
                      <div class="card border">
                        <div class="card-body text-center">
                          <h4 class="mb-0 text-success">ETB {{ formatAmount(memberUtilization.utilization.remaining_balance) }}</h4>
                          <p class="text-muted mb-0">Remaining Balance</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Service Breakdown -->
                  <div class="card border">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Service Utilization Breakdown</h6>
                    </div>
                    <div class="card-body">
                      <div class="table-responsive">
                        <table class="table table-sm">
                          <thead>
                          <tr>
                            <th>Service Type</th>
                            <th class="text-center">Count</th>
                            <th class="text-end">Amount</th>
                            <th class="text-end">Percentage</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="service in memberUtilization.service_breakdown" :key="service.type_name">
                            <td>{{ service.type_name }}</td>
                            <td class="text-center">{{ service.count }}</td>
                            <td class="text-end">ETB {{ formatAmount(service.amount) }}</td>
                            <td class="text-end">
                              {{ ((service.amount / memberUtilization.utilization.consumed_amount) * 100).toFixed(1) }}%
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hospital Documents Tab -->
              <div class="tab-pane" id="hospital-docs" role="tabpanel">
                <div class="alert alert-info">
                  <i class="bx bx-info-circle me-2"></i>
                  Hospital Reference: <strong>{{ claim.hospital_claim_reference }}</strong>
                </div>
                <p class="text-muted">Supporting documents from the hospital would be displayed here.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <span v-if="claim.status === 'approved'" class="text-success">
                <i class="bx bx-check-circle me-1"></i>
                Approved by {{ claim.approved_by }} on {{ formatDate(claim.approval_date) }}
              </span>
              <span v-else-if="claim.status === 'rejected'" class="text-danger">
                <i class="bx bx-x-circle me-1"></i>
                Rejected: {{ claim.rejection_reason }}
              </span>
            </div>
            <div>
              <button v-if="claim.status === 'submitted'" type="button" class="btn btn-success me-2"
                      @click="$emit('process', claim)">
                <i class="bx bx-check-circle me-1"></i>Process Claim
              </button>
              <button type="button" class="btn btn-secondary" @click="$emit('close')">
                <i class="bx bx-x me-1"></i>Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "ClaimDetailsModal",
  props: {
    claim: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      utilizationLoading: false,
      memberUtilization: null
    };
  },
  mounted() {
    this.loadMemberUtilization();
  },
  methods: {
    async loadMemberUtilization() {
      if (!this.claim.member_id) return;

      this.utilizationLoading = true;

      const data = {
        "function": "getMemberUtilization",
        "memberId": this.claim.member_id,
        "year": new Date(this.claim.service_date).getFullYear()
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.memberUtilization = res.data.data;
        }
      } catch (e) {
        console.error("Failed to load member utilization:", e);
      } finally {
        this.utilizationLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    getInitials(firstName, lastName) {
      return ((firstName || '')[0] || '') + ((lastName || '')[0] || '');
    },

    getStatusBadge(status) {
      const badges = {
        'submitted': 'badge bg-warning',
        'approved': 'badge bg-success',
        'rejected': 'badge bg-danger',
        'paid': 'badge bg-info'
      };
      return badges[status] || 'badge bg-secondary';
    },

    getPriorityBadge(days) {
      if (days > 7) return 'badge bg-danger';
      if (days >= 4) return 'badge bg-warning';
      return 'badge bg-primary';
    },

    getUtilizationBarClass(percentage) {
      if (percentage >= 90) return 'bg-danger';
      if (percentage >= 70) return 'bg-warning';
      return 'bg-success';
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 600;
  font-size: 1.5rem;
}

.nav-tabs-custom .nav-item .nav-link {
  border: none;
  font-weight: 600;
}

.nav-tabs-custom .nav-item .nav-link.active {
  border-bottom: 2px solid #556ee6;
  background-color: transparent;
}

.progress {
  background-color: #e9ecef;
}
</style>